import { t } from '@lingui/core/macro';
import { coerceUndefinedToNull } from '@luminovo/commons';
import { formatPercentage } from '@luminovo/frontend-utils';
import type { JSX } from 'react';
import { Controller, FieldValues, Path, ControllerProps as ReactHookFormControllerProps } from 'react-hook-form';

import { InputAdornment } from '../../InputAdornment';
import { BaseField } from '../BaseField';
import {
    fieldConverterInteger,
    fieldConverterIntegerString,
    fieldConverterNumber,
    fieldConverterPercent,
    fieldConverterRate,
    fieldConverterRateString,
    fieldConverterString,
    FieldNumericProps,
} from './FieldNumeric';

export interface FieldNumericControllerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<ReactHookFormControllerProps<TFieldValues, TName>, 'render' | 'rules' | 'defaultValues'> {
    FieldProps?: Omit<FieldNumericProps, 'value' | 'onChange' | 'required'>;
    required?: boolean;

    /**
     * The minimum (inclusive) value that this input can hold.
     *
     * @default -Infinity
     */
    min?: number;

    /**
     * The maximum (inclusive) value that this input can hold.
     *
     * @default Infinity
     */
    max?: number;

    /**
     * The output type. The string representation is suitable
     * for values that are not numerically significant (e.g., an internal reference number)
     * or decimals with a higher level of precision.
     *
     * The percent representation shows values to the user as a percentage (i.e. 0 to 100), but stores them as a decimal (i.e. 0 to 1).
     *
     * @default 'number'
     */
    representation?: 'number' | 'string' | 'percent';

    /**
     * @default false
     */
    isInteger?: boolean;

    /** Reads "1,125" and "1.125" as 1.125. For unit prices, exchange rates and percentages. */
    isRate?: boolean;

    validate?: (num: number) => string | undefined;
}

/** @deprecated Use `FieldNumericControlled` where possible. */
export function FieldNumericController<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
    min,
    max = Number.MAX_SAFE_INTEGER,
    validate,
    isInteger,
    isRate,
    required,
    representation = 'number',
    name,
    control,
    FieldProps = {},
    ...rest
}: FieldNumericControllerProps<TFieldValues, TName>): JSX.Element {
    const { converter, integerConverter, rateConverter, createValidator } = convertersByRepresentation[representation];
    return (
        <Controller
            name={name}
            control={control}
            rules={{
                validate: createValidator({
                    min,
                    max,
                    isInteger,
                    validate,
                    required,
                }),
            }}
            render={({ field, fieldState }) => {
                return (
                    <BaseField
                        // @ts-ignore
                        converter={isInteger ? integerConverter : isRate ? rateConverter : converter}
                        name={field.name}
                        inputRef={field.ref}
                        value={coerceUndefinedToNull(field.value as string | number | undefined)}
                        onChange={(value) => {
                            field.onChange(value);
                        }}
                        onBlur={() => field.onBlur()}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        {...FieldProps}
                        slotProps={{
                            ...FieldProps.slotProps,
                            input: {
                                autoComplete: 'off',
                                endAdornment:
                                    representation === 'percent' ? (
                                        <InputAdornment position="end">%</InputAdornment>
                                    ) : undefined,
                                ...FieldProps.slotProps?.input,
                            },
                        }}
                    />
                );
            }}
            {...rest}
        />
    );
}

const convertersByRepresentation = {
    number: {
        converter: fieldConverterNumber,
        integerConverter: fieldConverterInteger,
        rateConverter: fieldConverterRate,
        createValidator: createNumericValidator,
    },
    string: {
        converter: fieldConverterString,
        integerConverter: fieldConverterIntegerString,
        rateConverter: fieldConverterRateString,
        createValidator: createStringValidator,
    },
    percent: {
        converter: fieldConverterPercent,
        integerConverter: fieldConverterPercent,
        rateConverter: fieldConverterPercent,
        createValidator: createPercentValidator,
    },
};

export function createNumericValidator({
    min = -Infinity,
    max = Infinity,
    isInteger = false,
    required,
    validate,
}: Pick<FieldNumericControllerProps, 'min' | 'max' | 'isInteger' | 'validate' | 'required'>) {
    return (x: unknown): string | undefined => {
        if (!required && (x === null || x === undefined)) {
            return undefined;
        }
        if (x === null || x === undefined || typeof x !== 'number') {
            return t`Required`;
        }
        if (x < min) {
            return t`Must be greater than or equal to ${min}`;
        }
        if (x > max) {
            return t`Must be less than or equal to ${max}`;
        }
        if (isInteger && !Number.isInteger(x)) {
            return t`Must be a whole number`;
        }
        if (validate) {
            return validate(x);
        }
        return undefined;
    };
}

function createPercentValidator({
    min = -Infinity,
    max = Infinity,
    required,
    validate,
}: Pick<FieldNumericControllerProps, 'min' | 'max' | 'validate' | 'required'>) {
    return (x: unknown): string | undefined => {
        if (!required && (x === null || x === undefined)) {
            return undefined;
        }
        if (x === null || x === undefined || typeof x !== 'number') {
            return t`Required`;
        }
        if (x < min) {
            return t`Must be greater than or equal to ${formatPercentage(min)}`;
        }
        if (x > max) {
            return t`Must be less than or equal to ${formatPercentage(max)}`;
        }
        if (validate) {
            return validate(x);
        }
        return undefined;
    };
}

export function createStringValidator({
    min = -Infinity,
    max = Infinity,
    isInteger = false,
    required,
    validate,
}: Pick<FieldNumericControllerProps, 'min' | 'max' | 'isInteger' | 'validate' | 'required'>) {
    return (x: unknown): string | undefined => {
        if (!required && (x === null || x === undefined || x === '')) {
            return undefined;
        }
        if (x === null || x === undefined || x === '' || typeof x !== 'string') {
            return t`Required`;
        }
        const parsed = Number(x);
        return createNumericValidator({ min, max, isInteger, validate, required })(parsed);
    };
}
